import { action, observable, computed } from "mobx";
import BaseStore from "../base";
import { events } from "@app/lib/store";
import http from "@app/lib/http";
import { ws } from "@app/lib/socket";

import citationListStore from "../report/citation/list";
import citationPickStore from "../report/citation/pick";

class FileState {
    constructor(name) {
        this.name = name;
        this.status = "pending";
        this.progress = 0;
        this.error = null;
        this.message = null;
        this.citations = 0;
        this.skipped = 0;
    }
}

/**
 * Simplified state management for citation bulk upload
 * Following the simple upload pattern used in the application
 */
export class CitationBulkUploadStore extends BaseStore {
    @observable uploads = [];
    @observable modalState = { open: false };
    @observable resultModalOpen = false;
    @observable projectId = null;
    @observable activeTab = "pdf";

    constructor() {
        super();
        events.on("project.unload", () => {
            this.reset();
        });

        // Listen for simplified citation import events
        ws.on("citation.import.progress", (data) => {
            if (data.project !== this.projectId) {
                return;
            }

            this.updateProgress(data);
        });

        ws.on("citation.import.complete", (data) => {
            if (data.project !== this.projectId) {
                return;
            }

            this.updateComplete(data);
        });
    }

    @action
    async upload(filesList, projectId, fileType = "pdf") {
        this.uploads = [];
        this.projectId = projectId;
        this.uploads = filesList.map((fileWrapper) => new FileState(fileWrapper.name));

        for (const fileWrapper of filesList) {
            const file = fileWrapper.originFileObj || fileWrapper;
            this.uploads = this.uploads.map((upload) =>
                upload.name === file.name ? { ...upload, status: "loading" } : upload,
            );

            try {
                // Job queued, wait for WebSocket notifications
                this.uploads = this.uploads.map((upload) =>
                    upload.name === file.name
                        ? { ...upload, status: "queued", progress: 0 }
                        : upload,
                );

                if (fileType === "pdf") {
                    const formData = new FormData();
                    formData.append("files", file);
                    await http.post(`/project/${projectId}/citation/import/pdf`, formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                } else {
                    const formData = new FormData();
                    formData.append("file", file);
                    await http.post(`/project/${projectId}/citation/import/${fileType}`, formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                }
            } catch (error) {
                this.uploads = this.uploads.map((upload) =>
                    upload.name === file.name
                        ? {
                              ...upload,
                              status: "error",
                              error: true,
                              message: error.message,
                          }
                        : upload,
                );
            }
        }
    }

    @action
    updateProgress(data) {
        this.uploads = this.uploads.map((upload) =>
            upload.name === data.fileName
                ? {
                      ...upload,
                      status: "processing",
                      progress: data.progress,
                      processed: data.processed,
                      total: data.total,
                      created: data.created,
                      skipped: data.skipped,
                      message: data.message,
                      error: data.error,
                  }
                : upload,
        );
    }

    @action
    updateComplete(data) {
        this.uploads = this.uploads.map((upload) =>
            upload.name === data.fileName
                ? {
                      ...upload,
                      status: data.status,
                      progress: data.status === "success" ? 100 : 0,
                      citations: data.citations || 0,
                      skipped: data.skipped || 0,
                      error: data.error,
                      message: data.message,
                  }
                : upload,
        );

        // Show results modal when all uploads are complete
        const allComplete = this.uploads.every(
            (upload) => !["loading", "queued", "processing"].includes(upload.status),
        );
        if (allComplete) {
            this.toggleResultsModal(true);
        }

        // load references list
        this.loadReferences();
    }

    @action
    toggleModal(open = true) {
        this.modalState.open = open;
        if (!open) {
            this.reset();
        }
    }

    @action
    toggleResultsModal(open = true) {
        this.resultModalOpen = open;
    }

    @action
    reset() {
        this.uploads = [];
        this.modalState.open = false;
        this.resultModalOpen = false;
        this.projectId = null;
        this.activeTab = "pdf";
    }

    @computed
    get isLoading() {
        return this.uploads.some((upload) =>
            ["loading", "queued", "processing"].includes(upload.status),
        );
    }

    @computed
    get progress() {
        if (this.uploads.length === 0) {
            return 0;
        }

        const totalProgress = this.uploads.reduce((sum, upload) => sum + upload.progress, 0);
        return Math.round(totalProgress / this.uploads.length);
    }

    @action
    loadReferences() {
        events.emit("citations.update");

        if (citationListStore.project) {
            citationListStore.load();
        }

        if (citationPickStore.project) {
            citationPickStore.load();
        }
    }
}

export default new CitationBulkUploadStore();
